import { useEffect, useState } from 'react';

import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { skipToken } from '@reduxjs/toolkit/query';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import {
  EstimateStatusParams,
  useFetchEstimateStatusQuery,
} from '~/api/easy-estimator-api/estimate-api/estimateService';
import { shouldFetchFn } from '~/api/utils';
import { EE_STATUS, STATUS, STATUS_REFRESH } from '~/core/constants';
import { clamp } from '~/core/helpers';
import {
  EstimateStatus,
  GetEstimateDto,
} from '~/core/models/save-estimate-dto';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppSelector } from '~/redux/hooks';
import { selectEeStatusList } from '~/redux/slices/estimate';

type useEstimateStatusReturn = Omit<
  TypedUseQueryHookResult<
    EstimateStatus,
    EstimateStatusParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching'
> & {
  estimateStatus: EstimateStatus | undefined;
  isFetchingEstimateStatus: boolean;
};

const useEstimateStatus = (): useEstimateStatusReturn => {
  const [statusPollingInterval, setStatusPollingInterval] = useState(
    STATUS_REFRESH.OFF
  );
  const [statusPollingCount, setStatusPollingCount] = useState(0);

  const { fetchedEstimate } = useEstimateContext();
  const statusList = useAppSelector(selectEeStatusList);

  /**
   * Test if the fetched estimate needs to have its status checked or if we hae all the information we need.
   * Must exist, have an existing code, no paymentUrl, and status is OPEN or HOLD
   * @param estimate
   */
  const isEstimateStatusNeeded = (
    fetchedEstimate: GetEstimateDto | undefined
  ): boolean =>
    !!fetchedEstimate &&
    !!fetchedEstimate.code &&
    !fetchedEstimate.paymentUrl?.length &&
    (fetchedEstimate.status === STATUS.OPEN ||
      fetchedEstimate.status === STATUS.HOLD);

  const shouldRefetch = shouldFetchFn({
    args: [
      fetchedEstimate,
      statusPollingCount < STATUS_REFRESH.MAX_ATTEMPTS,
      isEstimateStatusNeeded(fetchedEstimate),
    ],
  });

  const {
    isFetching: isFetchingEstimateStatus,
    data: estimateStatus,
    ...rest
  } = useFetchEstimateStatusQuery(
    shouldRefetch
      ? {
          params: {
            code: fetchedEstimate?.code,
          },
        }
      : skipToken,
    {
      pollingInterval: statusPollingInterval * 1000,
    }
  );

  /**
   * Turn on status polling when we get a fetchedEstimate that needs status
   */
  useEffect(() => {
    if (fetchedEstimate && isEstimateStatusNeeded(fetchedEstimate)) {
      setStatusPollingInterval(STATUS_REFRESH.MIN); // start polling for status
    }
  }, [fetchedEstimate]);

  /**
   * Updates the status polling interval and stops updates if our status hits a final state
   */
  useEffect(() => {
    if (isFetchingEstimateStatus || !estimateStatus || !fetchedEstimate) {
      return;
    }

    const isStatusFinal =
      estimateStatus.paymentUrl !== null ||
      statusList.some((status) => status === EE_STATUS.CLOSED);

    setStatusPollingInterval(
      isStatusFinal
        ? STATUS_REFRESH.OFF // we're done, stop polling
        : (prevState) =>
            clamp(
              prevState * STATUS_REFRESH.BACK_OFF,
              STATUS_REFRESH.OFF,
              STATUS_REFRESH.MAX
            )
    );
    setStatusPollingCount((prevState) => prevState + 1);
  }, [isFetchingEstimateStatus, estimateStatus, fetchedEstimate, statusList]);

  return {
    estimateStatus,
    isFetchingEstimateStatus,
    ...rest,
  };
};

export default useEstimateStatus;
